<template>
  <div class="room-list-container">
    <h1>{{ t('room_list.title') }}</h1>
    <div class="actions">
      <button @click="openInviteCodeModal" class="invite-code-button">
        {{ t('room_list.join_by_invite_code') }}
      </button>
      <button @click="goToCreateRoom" class="create-room-button">
        {{ t('room_list.create_new_room') }}
      </button>
    </div>



    <div v-if="isLoading" class="loading-message">{{ t('common.loading') }}</div>
    <div v-else-if="error" class="error-message">
      {{ t('common.error_loading') }}: {{ error.message }}
    </div>
    <ul v-else-if="roomList.length" class="room-list">
      <li v-for="room in roomList" :key="room.id" class="room-item">
        <div class="room-info">
          <h2>{{ room.name }}</h2>
          <p>{{ t('room_create.game_mode_label') }}: {{ t('room_create.game_mode_' + room.room_type) }}</p>
          <p>{{ t('room_list.host') }}: {{ room.host.username }}</p>
          <p>{{ t('room_list.players') }}: {{ room.room_players ? room.room_players.length : room.current_players }} /
            {{ room.max_players }}</p>
          <p>{{ t('room_list.room_status_label') }}: {{ t(`room_status.${room.status}`) }}</p>
          <p>{{ t('room_list.created_at') }}: {{ formatDate(room.created_at) }}</p>
        </div>
        <div class="room-actions">
          <button @click="goToRoomDetail(room.id)" :disabled="room.status !== 'waiting'" class="join-room-button">
            {{ t('room_list.join_room') }}
          </button>
        </div>
      </li>
    </ul>
    <p v-else class="no-rooms-message">{{ t('room_list.no_rooms_available') }}</p>

    <!-- Invite Code Modal -->
    <div v-if="showInviteCodeModal" class="modal-overlay" @click.self="closeModals">
      <div class="modal">
        <h3>{{ t('room_list.invite_code_modal_title') }}</h3>
        <div class="modal-content invite-code-container">
          <label>{{ t('room_list.invite_code_label') }}</label>
          <div class="code-inputs">
            <input v-for="(digit, index) in inviteCodeDigits" :key="index" v-model="inviteCodeDigits[index]"
              :ref="(el) => { if (el) inputRefs[index] = el as HTMLInputElement }" type="text" maxlength="1"
              @input="handleCodeInput(index, $event)" @keydown.delete="handleCodeDelete(index, $event)"
              @paste="handlePaste" class="digit-input" />
          </div>
          <p v-if="inviteCodeError" class="validation-error">{{ inviteCodeError }}</p>
        </div>
      </div>
    </div>


  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useRoomStore } from '@/stores/room'
import { useI18n } from 'vue-i18n'

const router = useRouter()
const roomStore = useRoomStore()
const { t } = useI18n()

const isLoading = computed(() => roomStore.isLoading)
const error = computed(() => roomStore.error)
const roomList = computed(() => roomStore.roomList)

const showInviteCodeModal = ref(false)
const inviteCodeDigits = ref(['', '', '', '', ''])
const inputRefs = ref<HTMLInputElement[]>([])
const inviteCodeError = ref('')

const openInviteCodeModal = () => {
  showInviteCodeModal.value = true
  inviteCodeDigits.value = ['', '', '', '', '']
  inviteCodeError.value = ''
  // Focus first input on next tick
  setTimeout(() => {
    const firstInput = inputRefs.value[0]
    if (firstInput) firstInput.focus()
  }, 100)
}

const handleCodeInput = async (index: number, event: Event) => {
  const input = event.target as HTMLInputElement
  let value = input.value.toUpperCase()

  // Validate: only alphanumeric
  if (!/^[A-Z0-9]*$/.test(value)) {
    value = value.replace(/[^A-Z0-9]/g, '')
  }

  inviteCodeDigits.value[index] = value

  if (value && index < 4) {
    const nextInput = inputRefs.value[index + 1]
    if (nextInput) {
      nextInput.focus()
    }
  }

  // Check if complete
  if (index === 4 && value) {
    await submitInviteCode()
  }
}

const handleCodeDelete = (index: number) => {
  if (!inviteCodeDigits.value[index] && index > 0) {
    const prevInput = inputRefs.value[index - 1]
    if (prevInput) {
      prevInput.focus()
    }
  }
}

const handlePaste = async (event: ClipboardEvent) => {
  event.preventDefault()
  const pastedText = event.clipboardData?.getData('text').toUpperCase() || ''
  const cleanText = pastedText.replace(/[^A-Z0-9]/g, '')

  if (cleanText) {
    for (let i = 0; i < 5 && i < cleanText.length; i++) {
      // Ensure we assign a string, though cleanText[i] is string.
      inviteCodeDigits.value[i] = cleanText[i] || ''
    }

    // Focus logic
    const lastIndex = Math.min(cleanText.length, 5) - 1
    if (lastIndex < 4) {
      // focus next empty
      const nextInput = inputRefs.value[lastIndex + 1]
      if (nextInput) nextInput.focus()
    } else {
      // filled completely
      const lastInput = inputRefs.value[4]
      if (lastInput) lastInput.focus()
      await submitInviteCode()
    }
  }
}


const submitInviteCode = async () => {
  const code = inviteCodeDigits.value.join('')
  if (code.length !== 5) {
    inviteCodeError.value = t('room_list.empty_invite_code') || "Enter 5 digits"
    return
  }
  inviteCodeError.value = ''

  try {
    const response = await roomStore.joinRoomByInviteCode(code)
    if (response && response.roomId) {
      router.push({ name: 'RoomDetail', params: { roomId: response.roomId } })
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    console.error(e)
    if (e.response && e.response.data && e.response.data.error) {
      inviteCodeError.value = e.response.data.error
    } else {
      inviteCodeError.value = t('room_list.join_error') || "Error joining room"
    }
  }
}


const goToCreateRoom = () => {
  router.push({ name: 'RoomCreate' })
}

const goToRoomDetail = (roomId: string) => {
  router.push({ name: 'RoomDetail', params: { roomId: roomId } })
}

const closeModals = () => {
  showInviteCodeModal.value = false
  inviteCodeError.value = ''
  inviteCodeDigits.value = ['', '', '', '', '']
}
const formatDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }
  return new Date(dateString).toLocaleDateString(undefined, options)
}

onMounted(async () => {
  await roomStore.fetchRoomList()
  await roomStore.connectGlobalWebSocket()
})
</script>

<style scoped>
.room-list-container {
  max-width: 900px;
  margin: 50px auto;
  padding: 20px;
  border: 1px solid #eee;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  background-color: #f9f9f9;
  color: #333;
  font-family: 'Arial', sans-serif;
}

h1 {
  text-align: center;
  color: #2c3e50;
  margin-bottom: 30px;
  font-size: 2.5rem;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 20px;
}

.create-room-button,
.invite-code-button {
  padding: 10px 20px;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.3s ease;
}

.create-room-button {
  background-color: #28a745;
}

.create-room-button:hover {
  background-color: #218838;
}

.invite-code-button {
  background-color: #17a2b8;
}

.invite-code-button:hover {
  background-color: #138496;
}

.loading-message,
.error-message,
.no-rooms-message {
  text-align: center;
  margin-top: 20px;
  font-size: 1.1rem;
}

.error-message {
  color: #dc3545;
}

.no-rooms-message {
  color: #6c757d;
}

.room-list {
  list-style: none;
  padding: 0;
}

.room-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #ffffff;
  border: 1px solid #ddd;
  border-radius: 8px;
  margin-bottom: 15px;
  padding: 15px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease;
}

.room-item:hover {
  transform: translateY(-3px);
}

.room-info h2 {
  margin: 0 0 5px 0;
  color: #007bff;
  font-size: 1.5rem;
}

.room-info p {
  margin: 3px 0;
  font-size: 0.95rem;
  color: #555;
}

.room-actions .join-room-button {
  padding: 8px 15px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.3s ease;
}

.room-actions .join-room-button:hover:not(:disabled) {
  background-color: #0056b3;
}

.room-actions .join-room-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  width: 300px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  text-align: center;
}

.modal h3 {
  margin-top: 0;
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
  text-align: left;
}

.modal-content input {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.modal-actions {
  display: flex;
  justify-content: space-around;
}

.modal-actions button {
  padding: 8px 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.modal-actions button:first-child {
  background-color: #007bff;
  color: white;
}

.cancel-button {
  background-color: #6c757d;
  color: white;
}

.code-inputs {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin: 15px 0;
}

.digit-input {
  width: 40px;
  height: 50px;
  font-size: 24px;
  text-align: center;
  border: 1px solid #ccc;
  border-radius: 5px;
  text-transform: uppercase;
}

.validation-error {
  color: red;
  font-size: 0.9rem;
  margin-top: 5px;
  text-align: center;
}

.invite-code-container {
  align-items: center;
}
</style>
